import React, { useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  Plus,
  Zap,
  SquareCheckBig,
  Calendar,
  Bell,
  LogOut,
  ArrowUpRight,
} from "lucide-react";

const ACTIONS = [
  {
    id: "task",
    label: "New Task",
    icon: SquareCheckBig,
    glow: "from-blue-500/20 to-cyan-500/20",
    border: "border-blue-500/30 group-hover:border-blue-500/50",
    bgHover: "group-hover:bg-white/10",
    iconBg: "from-blue-500 to-blue-600",
    textHover: "group-hover:text-blue-300",
    arrow: "text-blue-400",
  },
  {
    id: "event",
    label: "New Event",
    icon: Calendar,
    glow: "from-purple-500/20 to-blue-500/20",
    border: "border-purple-500/30 group-hover:border-purple-500/50",
    bgHover: "group-hover:bg-white/10",
    iconBg: "from-purple-500 to-purple-600",
    textHover: "group-hover:text-purple-300",
    arrow: "text-purple-400",
  },
  {
    id: "reminder",
    label: "New Reminder",
    icon: Bell,
    glow: "from-cyan-500/20 to-teal-500/20",
    border: "border-cyan-500/30 group-hover:border-cyan-500/50",
    bgHover: "group-hover:bg-white/10",
    iconBg: "from-cyan-500 to-teal-500",
    textHover: "group-hover:text-cyan-300",
    arrow: "text-cyan-400",
  },
  {
    id: "checkin",
    label: "Check In",
    subLabel: "0s",
    icon: LogOut,
    glow: "from-gray-500/20 to-slate-500/20",
    border: "border-gray-500/30 group-hover:border-gray-500/50",
    bgHover: "group-hover:bg-white/10",
    iconBg: "from-gray-500 to-slate-600",
    textHover: "group-hover:text-gray-300",
    arrow: "text-gray-400",
  },
];

function ActionRow({ item, onClick }) {
  const Icon = item.icon;

  return (
    <button
      type="button"
      onClick={() => onClick?.(item)}
      className="w-full group relative outline-none"
    >
      {/* glow */}
      <div
        className={`absolute inset-0 rounded-2xl blur-xl group-hover:blur-2xl transition-all bg-gradient-to-r ${item.glow}`}
      />

      {/* row */}
      <div
        className={`relative bg-white/5 backdrop-blur-sm border-2 rounded-2xl px-6 py-4 flex items-center gap-4 transition-all ${item.border} ${item.bgHover}`}
      >
        {/* icon box */}
        <div
          className={`w-12 h-12 bg-gradient-to-br ${item.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>

        {/* text */}
        {item.subLabel ? (
          <div className="flex-1 text-left">
            <span
              className={`text-xl block transition-colors text-white ${item.textHover}`}
            >
              {item.label}
            </span>
            <span className="text-sm text-gray-400 mt-1 block">
              {item.subLabel}
            </span>
          </div>
        ) : (
          <span
            className={`text-xl text-white transition-colors ${item.textHover}`}
          >
            {item.label}
          </span>
        )}

        {/* arrow */}
        <ArrowUpRight
          className={`w-5 h-5 ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all ${item.arrow}`}
        />
      </div>
    </button>
  );
}

export default function QuickActionsModal({ open, onClose, onAction }) {
  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
        >
          {/* overlay */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* modal */}
          <motion.div
            className="relative w-full max-w-[560px] bg-gradient-to-br from-gray-900 to-black rounded-3xl p-8 border border-cyan-500/30 shadow-2xl"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
          >
            {/* close */}
            <button
              type="button"
              onClick={onClose}
              className="absolute top-6 right-6 w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors group"
              aria-label="Close"
            >
              <Plus className="w-6 h-6 text-gray-400 group-hover:text-white rotate-45 transition-colors" />
            </button>

            {/* header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 text-cyan-400 px-4 py-2 rounded-full mb-4">
                <Zap className="w-4 h-4" />
                <span className="text-sm">Lightning Fast Actions</span>
              </div>

              <h2 className="text-4xl text-white mb-2">
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Quick Actions
                </span>
              </h2>

              <p className="text-gray-400">
                Access your most common actions instantly
              </p>
            </div>

            {/* actions */}
            <div className="space-y-3">
              {ACTIONS.map((item) => (
                <ActionRow
                  key={item.id}
                  item={item}
                  onClick={(x) => onAction?.(x)}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}